import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCatalog } from "@/data/CatalogContext";
import { getSimulatedInventory } from "@/data/skuMapping";
import type { SkuCategory } from "@/data/skuMapping";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Package, Link2, Layers, Database, MoreHorizontal, Pencil, Trash2, Plus, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS: Record<SkuCategory, string> = {
  base: "Base", walls: "Walls", glass: "Glass", door: "Door",
  fixture: "Fixture", accessory: "Accessory", trim: "Trim", hardware: "Hardware",
};

type AdminTab = "rules" | "bundles";

export function SkuMappingAdmin() {
  const { mappingRules, setMappingRules, bundles, setBundles, skuCatalog } = useCatalog();
  const [tab, setTab] = useState<AdminTab>("rules");

  const tabs: { id: AdminTab; label: string; icon: React.ElementType }[] = [
    { id: "rules", label: "Mapping Rules", icon: Link2 },
    { id: "bundles", label: "Bundles", icon: Layers },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold">System-to-SKU Mapping</h3>
          <p className="text-xs text-muted-foreground">Control layer for mapping configuration selections to SKUs</p>
        </div>
        <Badge variant="outline" className="text-[9px] gap-1">
          <Database className="h-2.5 w-2.5" /> Integration Ready
        </Badge>
      </div>

      <div className="flex gap-1 border-b pb-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
              tab === t.id ? "bg-background border border-b-0 text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setTab(t.id)}
          >
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* Mapping Rules */}
      {tab === "rules" && (
        <div className="space-y-3">
          <p className="text-[10px] text-muted-foreground">
            Rules define which SKUs are required based on project configuration selections.
          </p>
          <div className="space-y-2">
            {mappingRules.map((rule) => (
              <Card key={rule.id} className="shadow-none">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-bold">{rule.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule.conditions.systemType && <Badge variant="outline" className="text-[9px]">System: {rule.conditions.systemType}</Badge>}
                        {rule.conditions.material && <Badge variant="outline" className="text-[9px]">Material: {rule.conditions.material}</Badge>}
                        {rule.conditions.baseWidth && rule.conditions.baseLength && <Badge variant="outline" className="text-[9px]">Size: {rule.conditions.baseWidth}×{rule.conditions.baseLength}</Badge>}
                        {rule.conditions.componentCategory && <Badge variant="outline" className="text-[9px]">Category: {rule.conditions.componentCategory}</Badge>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground">Produces</p>
                      {rule.skuItems.map((si) => (
                        <p key={si.sku} className="text-[10px] font-mono">{si.sku} ×{si.quantity}</p>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Bundles */}
      {tab === "bundles" && (
        <div className="space-y-3">
          <p className="text-[10px] text-muted-foreground">
            Bundles group related SKUs into complete system packages. Manage bundles in Procurement → Catalog.
          </p>
          {bundles.map((bundle) => (
            <Card key={bundle.id} className="shadow-none">
              <CardContent className="p-3">
                <p className="text-xs font-bold">{bundle.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{bundle.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {bundle.skus.map((sku) => {
                    const item = skuCatalog.find((c) => c.sku === sku);
                    return (
                      <Badge key={sku} variant="outline" className="text-[9px] font-mono gap-1">
                        {sku} {item && <span className="text-muted-foreground">({item.itemName})</span>}
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
